/**
 * Logo Upload Service
 *
 * Handles the complete logo upload workflow:
 * 1. Validate file (size, type, dimensions)
 * 2. Optimize for invoice and preview variants
 * 3. Upload to Firebase Storage with deterministic paths
 * 4. Clean up previous versions
 * 5. Return metadata for Firestore
 */

import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { storage, functions } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import { validateLogoFile, SVG_MIME_TYPE } from './logoValidation';
import { optimizeLogo, blobToFile } from './logoOptimization';

// ============================================================================
// Constants
// ============================================================================

/** Base storage path for company logos */
const LOGO_STORAGE_BASE = 'branding';

/** Cache control header for logo files (1 year, immutable) */
const CACHE_CONTROL = 'public, max-age=31536000, immutable';

// ============================================================================
// Types
// ============================================================================

export interface LogoMetadata {
  /** URL for invoice rendering (high quality) */
  invoiceUrl: string;
  /** URL for UI preview (smaller) */
  previewUrl: string;
  /** Storage path for invoice variant */
  invoiceStoragePath: string;
  /** Storage path for preview variant */
  previewStoragePath: string;
  /** Original image width */
  originalWidth: number;
  /** Original image height */
  originalHeight: number;
  /** Invoice variant width */
  invoiceWidth: number;
  /** Invoice variant height */
  invoiceHeight: number;
  /** Whether image has transparency */
  hasAlpha: boolean;
  /** Content type of stored files */
  contentType: string;
  /** Total bytes stored (invoice + preview) */
  totalBytes: number;
  /** Upload timestamp */
  uploadedAt: Date;
  /** Original format (e.g., 'svg' if rasterized from SVG) */
  originalFormat?: string;
}

/** Response from the rasterizeSvgLogo Cloud Function */
interface RasterizeSvgResponse {
  success: true;
  invoiceUrl: string;
  previewUrl: string;
  invoiceStoragePath: string;
  previewStoragePath: string;
  originalWidth: number;
  originalHeight: number;
  invoiceWidth: number;
  invoiceHeight: number;
}

interface RasterizeSvgError {
  success: false;
  error: string;
  code: string;
}

export interface UploadLogoResult {
  success: true;
  metadata: LogoMetadata;
}

export interface UploadLogoError {
  success: false;
  error: string;
  details?: string;
}

// ============================================================================
// Main Upload Function
// ============================================================================

/**
 * Upload and optimize a company logo
 *
 * @param teamId - Team ID
 * @param profileId - Company profile ID
 * @param file - Logo file to upload
 * @returns Upload result with metadata or error
 */
export async function uploadOptimizedLogo(
  teamId: string,
  profileId: string,
  file: File
): Promise<UploadLogoResult | UploadLogoError> {
  logger.log('[logoUploadService] Starting upload for team:', teamId, 'profile:', profileId);

  try {
    // Step 1: Validate file
    const validation = await validateLogoFile(file);
    if (!validation.valid) {
      return {
        success: false,
        error: validation.error?.message || 'Validation failed',
        details: validation.error?.details,
      };
    }

    // Step 2: Check if SVG (requires server-side processing)
    if (validation.requiresServerProcessing || file.type === SVG_MIME_TYPE) {
      logger.log('[logoUploadService] SVG detected, routing to server-side rasterization');
      return uploadSvgLogo(teamId, profileId, file);
    }

    // Step 3: Optimize logo (create invoice and preview variants) - raster formats only
    const optimized = await optimizeLogo(file);

    // Step 3: Clean up previous logo files BEFORE uploading new ones
    // This ensures no race condition between delete and upload
    // Must complete fully before proceeding to avoid orphaned files
    await cleanupPreviousLogos(teamId, profileId);
    logger.log('[logoUploadService] Cleanup complete, starting uploads');

    // Step 4: Determine file extension based on contentType (source of truth)
    const ext = optimized.invoice.format === 'image/png' ? 'png' : 'webp';

    // Step 5: Upload invoice variant
    // Note: getDownloadURL returns a URL with a unique token that changes on each upload
    // This guarantees cache busting - browsers will fetch the new file even with immutable cache
    const invoiceStoragePath = `${LOGO_STORAGE_BASE}/${teamId}/profiles/${profileId}/logo.${ext}`;
    const invoiceFile = blobToFile(optimized.invoice.blob, 'logo', 'invoice');
    const invoiceUrl = await uploadFile(invoiceStoragePath, invoiceFile);

    // Step 6: Upload preview variant (sequential to avoid race conditions)
    const previewStoragePath = `${LOGO_STORAGE_BASE}/${teamId}/profiles/${profileId}/logo-preview.${ext}`;
    const previewFile = blobToFile(optimized.preview.blob, 'logo', 'preview');
    const previewUrl = await uploadFile(previewStoragePath, previewFile);

    // Step 7: Build metadata
    const metadata: LogoMetadata = {
      invoiceUrl,
      previewUrl,
      invoiceStoragePath,
      previewStoragePath,
      originalWidth: optimized.originalWidth,
      originalHeight: optimized.originalHeight,
      invoiceWidth: optimized.invoice.width,
      invoiceHeight: optimized.invoice.height,
      hasAlpha: optimized.hasAlpha,
      contentType: optimized.invoice.format,
      totalBytes: optimized.invoice.blob.size + optimized.preview.blob.size,
      uploadedAt: new Date(),
    };

    logger.log('[logoUploadService] Upload complete:', {
      invoiceSize: optimized.invoice.blob.size,
      previewSize: optimized.preview.blob.size,
      format: ext,
    });

    return { success: true, metadata };
  } catch (error) {
    logger.error('[logoUploadService] Upload failed:', error);
    return {
      success: false,
      error: 'Upload mislukt',
      details: error instanceof Error ? error.message : 'Onbekende fout',
    };
  }
}

/**
 * Upload a file to Firebase Storage with cache headers
 */
async function uploadFile(storagePath: string, file: File): Promise<string> {
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file, {
    contentType: file.type,
    cacheControl: CACHE_CONTROL,
    customMetadata: {
      uploadedAt: new Date().toISOString(),
    },
  });

  return getDownloadURL(storageRef);
}

/**
 * Clean up previous logo files for a profile
 * Deletes all files in the profile's logo directory
 */
async function cleanupPreviousLogos(teamId: string, profileId: string): Promise<void> {
  const basePath = `${LOGO_STORAGE_BASE}/${teamId}/profiles/${profileId}`;
  const folderRef = ref(storage, basePath);

  try {
    const result = await listAll(folderRef);

    // Delete all existing files
    const deletePromises = result.items.map(async (itemRef) => {
      try {
        await deleteObject(itemRef);
        logger.log('[logoUploadService] Deleted previous file:', itemRef.fullPath);
      } catch (err) {
        // Ignore errors for individual files
        logger.warn('[logoUploadService] Failed to delete:', itemRef.fullPath, err);
      }
    });

    await Promise.all(deletePromises);

    if (result.items.length > 0) {
      logger.log('[logoUploadService] Cleaned up', result.items.length, 'previous files');
    }
  } catch {
    // Folder might not exist yet, which is fine
    logger.log('[logoUploadService] No previous files to clean up');
  }
}

/**
 * Delete all logo files for a profile
 * Called when removing a logo or deleting a profile
 */
export async function deleteProfileLogos(teamId: string, profileId: string): Promise<void> {
  await cleanupPreviousLogos(teamId, profileId);
  logger.log('[logoUploadService] Deleted all logos for profile:', profileId);
}

/**
 * Get the primary logo URL for a profile
 * Returns the invoice URL (high quality) for backward compatibility
 */
export function getPrimaryLogoUrl(metadata: LogoMetadata | undefined): string | undefined {
  return metadata?.invoiceUrl;
}

// ============================================================================
// SVG Upload (Server-Side Processing)
// ============================================================================

/**
 * Upload an SVG logo via server-side rasterization
 *
 * Flow:
 * 1. Upload SVG to temporary storage path
 * 2. Call Cloud Function to rasterize SVG to PNG
 * 3. Cloud Function uploads PNG variants and returns URLs
 * 4. Return metadata for Firestore
 */
async function uploadSvgLogo(
  teamId: string,
  profileId: string,
  file: File
): Promise<UploadLogoResult | UploadLogoError> {
  logger.log('[logoUploadService] Starting SVG upload for team:', teamId, 'profile:', profileId);

  try {
    // Step 1: Clean up previous logo files first
    await cleanupPreviousLogos(teamId, profileId);
    logger.log('[logoUploadService] Cleanup complete, uploading SVG to temp path');

    // Step 2: Upload SVG to temporary storage path
    const tempPath = `${LOGO_STORAGE_BASE}/${teamId}/profiles/${profileId}/temp-logo.svg`;
    const tempRef = ref(storage, tempPath);

    await uploadBytes(tempRef, file, {
      contentType: SVG_MIME_TYPE,
      customMetadata: {
        uploadedAt: new Date().toISOString(),
        isTemporary: 'true',
      },
    });
    logger.log('[logoUploadService] SVG uploaded to temp path:', tempPath);

    // Step 3: Call Cloud Function to rasterize SVG
    const rasterizeSvg = httpsCallable<
      { teamId: string; profileId: string; tempSvgPath: string },
      RasterizeSvgResponse | RasterizeSvgError
    >(functions, 'rasterizeSvgLogo');

    const result = await rasterizeSvg({
      teamId,
      profileId,
      tempSvgPath: tempPath,
    });

    const response = result.data;

    if (!response.success) {
      logger.error('[logoUploadService] SVG rasterization failed:', response.error);
      return {
        success: false,
        error: response.error || 'SVG rasterization failed',
        details: `Code: ${response.code}`,
      };
    }

    // Step 4: Build metadata from Cloud Function response
    const metadata: LogoMetadata = {
      invoiceUrl: response.invoiceUrl,
      previewUrl: response.previewUrl,
      invoiceStoragePath: response.invoiceStoragePath,
      previewStoragePath: response.previewStoragePath,
      originalWidth: response.originalWidth,
      originalHeight: response.originalHeight,
      invoiceWidth: response.invoiceWidth,
      invoiceHeight: response.invoiceHeight,
      hasAlpha: true, // SVG always supports transparency
      contentType: 'image/png', // Rasterized to PNG
      totalBytes: 0, // Unknown from Cloud Function
      uploadedAt: new Date(),
      originalFormat: 'svg',
    };

    logger.log('[logoUploadService] SVG upload complete:', {
      originalWidth: response.originalWidth,
      originalHeight: response.originalHeight,
      invoiceWidth: response.invoiceWidth,
      invoiceHeight: response.invoiceHeight,
    });

    return { success: true, metadata };
  } catch (error) {
    logger.error('[logoUploadService] SVG upload failed:', error);
    return {
      success: false,
      error: 'SVG upload mislukt',
      details: error instanceof Error ? error.message : 'Onbekende fout',
    };
  }
}
